import { Box } from '@mui/material';
import { gridGapSx } from '../../theme';
import { PhaseCard } from '../PhaseCard';
import { StageHeader } from '../StageHeader';
import type { StageColumnProps } from './types';

/**
 * One column of the L1 grid: a stage header and the phase cards under it.
 *
 * **The cards are a grid of equal rows, not a flex stack, and that is the whole
 * reason this is a component.** `flex: 1` looks equivalent and is not — flex
 * items default to `min-height: auto`, so a card whose title wraps keeps its
 * content height while its siblings keep their equal share, and the rows stop
 * lining up across columns. It shows only at the widths where one title wraps
 * and another does not, around 1213px and again below 1024px.
 *
 * **A stage with fewer phases stretches to fill.** Discover's one card spans all
 * three of Design's rows rather than floating at the top of an empty column.
 */

/**
 * How many rows each card takes, spreading `count` cards over `rowCount` as
 * evenly as the division allows — one over three spans all three, two over three
 * spans one then two.
 *
 * The difference between two floors, so the spans sum to `rowCount` exactly: a
 * rounding rule that lost a row would leave a gap in one column and nowhere else.
 */
const rowSpans = (count: number, rowCount: number): number[] =>
  Array.from({ length: count }, (_, index) =>
    Math.floor(((index + 1) * rowCount) / count) - Math.floor((index * rowCount) / count),
  );

export const StageColumn = ({ group, rowCount, fullWidthAtTablet = false }: StageColumnProps) => {
  const spans = rowSpans(group.phases.length, rowCount);
  const columnRows = `repeat(${String(rowCount)}, 1fr)`;
  const phaseColumns = `repeat(${String(group.phases.length)}, minmax(0, 1fr))`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...gridGapSx,
        // So the stage that would otherwise sit alone on a row uses all of it.
        ...(fullWidthAtTablet ? { gridColumn: { xs: 'auto', sm: '1 / -1', md: 'auto' } } : {}),
      }}
    >
      <StageHeader stage={group.stage} />

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          flex: 1,
          display: 'grid',
          ...gridGapSx,
          // Stacked everywhere except where a stage has the full width — there
          // its phases run across instead of down.
          ...(fullWidthAtTablet
            ? {
                gridTemplateColumns: { xs: '1fr', sm: phaseColumns, md: '1fr' },
                gridTemplateRows: { xs: columnRows, sm: 'auto', md: columnRows },
              }
            : { gridTemplateRows: columnRows }),
        }}
      >
        {group.phases.map((phase, index) => {
          const span = `span ${String(spans[index] ?? 1)}`;

          return (
            <Box
              component="li"
              key={phase.id}
              sx={{
                display: 'flex',
                // The row spans stretch a short column to the grid's height.
                // Laid out across, there is nothing to stretch.
                gridRow: fullWidthAtTablet ? { xs: span, sm: 'auto', md: span } : span,
              }}
            >
              <PhaseCard phase={phase} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
